import { useCallback, useState } from 'react';

import type { EmailRequestFormValues } from '@/features/auth/password-reset/components/EmailRequestForm';
import type { VerificationCodeFormValues } from '@/features/auth/password-reset/components/VerificationCodeForm';
import { CLERK_RESOURCE_STATUS, CLERK_STRATEGY } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { useSignIn } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

type PasswordResetStep = 'email' | 'code';

export const usePasswordReset = () => {
  const { signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [step, setStep] = useState<PasswordResetStep>('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);

  const handleReenterEmail = useCallback(() => {
    setStep('email');
    setEmail('');
    setError(null);
  }, []);

  const handleEmailSubmit = useCallback(
    async (data: EmailRequestFormValues) => {
      if (!signIn) return false;

      setLoading(true);
      setError(null);

      try {
        await signIn.create({
          strategy: CLERK_STRATEGY.RESET_PASSWORD_EMAIL_CODE,
          identifier: data.email
        });

        setEmail(data.email);
        setStep('code');
        return true;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signIn, t]
  );

  const handleCodeSubmit = useCallback(
    async (data: VerificationCodeFormValues) => {
      if (!signIn || !email) return false;

      setLoading(true);
      setError(null);

      let shouldCleanup = true;

      try {
        const result = await signIn.attemptFirstFactor({
          strategy: CLERK_STRATEGY.RESET_PASSWORD_EMAIL_CODE,
          code: data.code,
          password: data.password
        });

        if (result.status === CLERK_RESOURCE_STATUS.COMPLETE) {
          await setActive({ session: result.createdSessionId });
          shouldCleanup = false;
          toast.success(t('success.passwordResetSent.reset'));
          void navigate('/');
          return true;
        }

        setError({ type: 'general', message: t('error.passwordResetSubmit.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        if (shouldCleanup) {
          setLoading(false);
        }
      }
    },
    [signIn, setActive, email, navigate, t]
  );

  return {
    step,
    email,
    error,
    loading,
    handleEmailSubmit,
    handleCodeSubmit,
    handleReenterEmail
  };
};
